import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconBadgeComponent, IconBadgeVariant, IconBadgeSize, IconBadgeShape } from '../icon-badge/icon-badge.component';

export type StatsCardVariant = 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'indigo' | 'gray' | 'cyan' | 'orange' | 'pink';

/**
 * StatsCard Component - Card displaying a stat with label, value and icon
 *
 * @example
 * <!-- Ongoing Projects card -->
 * <app-atom-stats-card
 *   label="Ongoing Projects"
 *   [value]="4"
 *   icon="control-play"
 *   variant="blue">
 * </app-atom-stats-card>
 *
 * <!-- Completed card -->
 * <app-atom-stats-card
 *   label="Completed Projects"
 *   [value]="completedCount()"
 *   icon="check"
 *   variant="green">
 * </app-atom-stats-card>
 */
@Component({
  selector: 'app-atom-stats-card',
  standalone: true,
  imports: [CommonModule, IconBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [class]="cardClasses()">
      <div class="flex-1">
        <p class="text-sm text-gray-500 mb-1">{{ label() }}</p>
        <p class="text-3xl font-bold text-gray-900">{{ formattedValue() }}</p>
        @if (subtitle()) {
          <p class="text-xs text-gray-400 mt-1">{{ subtitle() }}</p>
        }
      </div>
      <app-atom-icon-badge
        [icon]="icon()"
        [variant]="variant()"
        [size]="iconSize()"
        [shape]="iconShape()">
      </app-atom-icon-badge>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class StatsCardComponent {
  /** Label text above the value */
  label = input.required<string>();
  
  /** The stat value (number or string) */
  value = input.required<number | string>();
  
  /** Icon name for the icon badge */
  icon = input<string>('folder');
  
  /** Color variant */
  variant = input<StatsCardVariant>('blue');
  
  /** Optional subtitle text below value */
  subtitle = input<string>('');
  
  /** Size of the icon badge */
  iconSize = input<IconBadgeSize>('lg');
  
  /** Shape of the icon badge */
  iconShape = input<IconBadgeShape>('rounded');
  
  /** Whether card has border */
  bordered = input<boolean>(true);
  
  /** Additional CSS classes */
  additionalClasses = input<string>('');

  /** Format the value (add commas for large numbers) */
  formattedValue = computed(() => {
    const val = this.value();
    if (typeof val === 'number') {
      return val.toLocaleString();
    }
    return val;
  });

  /** Computed card classes */
  cardClasses = computed(() => {
    const classes = [
      'bg-white',
      'rounded-xl',
      'p-5',
      'flex',
      'items-center',
      'justify-between'
    ];
    
    if (this.bordered()) {
      classes.push('border', 'border-gray-200');
    } else {
      classes.push('shadow-sm');
    }
    
    if (this.additionalClasses()) {
      classes.push(this.additionalClasses());
    }
    
    return classes.join(' ');
  });
}
